import React, { useState, useEffect, useRef } from 'react';
import {
  FiMail,
  FiMessageCircle,
  FiSmartphone,
  FiX,
  FiSend,
  FiCheckCircle,
  FiAlertCircle,
  FiInfo,
  FiMessageSquare,
} from 'react-icons/fi';
import { communicationApi } from '../services/communicationApi';
import CannedResponsePicker from './CannedResponsePicker';
import { appendCannedResponseContent, useCannedResponseShortcuts } from '../hooks/useCannedResponseShortcuts';
import './MultiChannelMessageSender.css';

// Icon wrapper to fix TypeScript compatibility
const Icon = ({ icon: IconComponent, className, style }: { icon: any; className?: string; style?: React.CSSProperties }) =>
  React.createElement(IconComponent as any, { className, style });

interface MultiChannelMessageSenderProps {
  userId: number;
  userName: string;
  userEmail?: string;
  userPhone?: string;
  onClose: () => void;
  onSent?: (channel: string) => void;
}

const MultiChannelMessageSender: React.FC<MultiChannelMessageSenderProps> = ({
  userId,
  userName,
  userEmail,
  userPhone,
  onClose,
  onSent,
}) => {
  const [selectedChannel, setSelectedChannel] = useState<'email' | 'whatsapp' | 'sms' | null>(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showCannedResponsePicker, setShowCannedResponsePicker] = useState(false);
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);

  useCannedResponseShortcuts(message, setMessage);
  const [availableChannels, setAvailableChannels] = useState<{
    email?: string;
    phone?: string;
    whatsapp?: string;
    channels: Array<'email' | 'whatsapp' | 'sms'>;
  } | null>(null);
  const [loadingChannels, setLoadingChannels] = useState(true);

  useEffect(() => {
    loadAvailableChannels();
  }, [userId]);

  const loadAvailableChannels = async () => {
    try {
      setLoadingChannels(true);
      const response = await communicationApi.getAvailableChannels(userId);
      if (response.success && response.data) {
        setAvailableChannels(response.data);
      }
    } catch (error: any) {
      console.error('Failed to load available channels:', error);
    } finally {
      setLoadingChannels(false);
    }
  };

  const handleSend = async () => {
    if (!selectedChannel || !message.trim()) {
      alert('Please select a channel and enter a message');
      return;
    }

    // Get the actual contact info (email/phone) for the selected channel
    let toAddress: string | undefined;
    switch (selectedChannel) {
      case 'email':
        toAddress = availableChannels?.email || userEmail;
        break;
      case 'whatsapp':
        toAddress = availableChannels?.whatsapp || userPhone;
        break;
      case 'sms':
        toAddress = availableChannels?.phone || userPhone;
        break;
    }

    if (!toAddress || toAddress === 'Not available') {
      alert(`❌ ${selectedChannel.toUpperCase()} contact information not available for this user`);
      return;
    }

    try {
      setSending(true);
      const response = await communicationApi.sendMessage({
        userId, // Keep for fallback to admin endpoint
        to: toAddress, // Actual contact info (email/phone)
        channel: selectedChannel,
        subject: selectedChannel === 'email' ? subject : undefined,
        message: message.trim(),
      });

      if (response.success) {
        alert(`✅ Message sent via ${selectedChannel.toUpperCase()} successfully!`);
        setMessage('');
        setSubject('');
        setSelectedChannel(null);
        if (onSent) {
          onSent(selectedChannel);
        }
        onClose();
      } else {
        alert(`❌ Failed to send message: ${response.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to send message. Check console for details.';
      alert(`Error: ${errorMessage}`);
    } finally {
      setSending(false);
    }
  };

  const handleInsertCannedResponse = (content: string) => {
    setMessage(prev => appendCannedResponseContent(prev, content));
    setTimeout(() => messageTextareaRef.current?.focus(), 100);
  };

  const getChannelInfo = (channel: 'email' | 'whatsapp' | 'sms') => {
    switch (channel) {
      case 'email':
        return {
          icon: FiMail,
          name: 'Email',
          address: availableChannels?.email || userEmail || 'Not available',
          color: '#3b82f6',
        };
      case 'whatsapp':
        return {
          icon: FiMessageCircle,
          name: 'WhatsApp',
          address: availableChannels?.whatsapp || userPhone || 'Not available',
          color: '#25d366',
        };
      case 'sms':
        return {
          icon: FiSmartphone,
          name: 'SMS',
          address: availableChannels?.phone || userPhone || 'Not available',
          color: '#06b6d4',
        };
    }
  };

  const channels = availableChannels?.channels || [];

  return (
    <div className="multi-channel-sender">
      <div className="mcs-header">
        <div className="mcs-header-left">
          <h3>Send Message to {userName}</h3>
          <p>Choose a communication channel</p>
        </div>
        <button className="btn-close" onClick={onClose}>
          <Icon icon={FiX} />
        </button>
      </div>

      {loadingChannels ? (
        <div className="mcs-loading">
          <p>Loading available channels...</p>
        </div>
      ) : (
        <>
          {/* Channel Selection */}
          <div className="mcs-channels">
            {channels.map(channel => {
              const info = getChannelInfo(channel);
              const IconComponent = info.icon;
              const isAvailable = info.address !== 'Not available';

              return (
                <button
                  key={channel}
                  className={`channel-option ${selectedChannel === channel ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                  onClick={() => isAvailable && setSelectedChannel(channel)}
                  disabled={!isAvailable}
                  title={!isAvailable ? `${info.name} not available for this user` : `Send via ${info.name}`}
                >
                  <Icon icon={IconComponent} style={{ color: info.color }} />
                  <div className="channel-info">
                    <span className="channel-name">{info.name}</span>
                    <span className="channel-address">{info.address}</span>
                  </div>
                  {selectedChannel === channel && (
                    <Icon icon={FiCheckCircle} className="check-icon" />
                  )}
                </button>
              );
            })}

            {channels.length === 0 && (
              <div className="no-channels">
                <Icon icon={FiAlertCircle} />
                <p>No communication channels available for this user</p>
              </div>
            )}
          </div>

          {/* Message Form */}
          {selectedChannel && (
            <div className="mcs-form">
              {selectedChannel === 'email' && (
                <div className="form-field">
                  <label>Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder="Email subject..."
                  />
                </div>
              )}

              <div className="form-field">
                <div className="mcs-message-field-header">
                  <label>Message</label>
                  <button
                    type="button"
                    className="mcs-canned-btn"
                    onClick={() => setShowCannedResponsePicker(true)}
                    title="Insert canned response (type /shortcut + space)"
                  >
                    <Icon icon={FiMessageSquare} />
                    Canned
                  </button>
                </div>
                <textarea
                  ref={messageTextareaRef}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={`Type /shortcut + space or your ${selectedChannel === 'email' ? 'email' : selectedChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'} message...`}
                  rows={6}
                />
                <CannedResponsePicker
                  anchorEl={messageTextareaRef.current}
                  open={showCannedResponsePicker}
                  onClose={() => setShowCannedResponsePicker(false)}
                  onSelect={handleInsertCannedResponse}
                  placement="top"
                />
                {selectedChannel === 'sms' && (
                  <span className="field-hint">
                    SMS messages are limited to 160 characters
                  </span>
                )}
              </div>

              <div className="mcs-actions">
                <button className="btn-secondary" onClick={onClose} disabled={sending}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSend}
                  disabled={sending || !message.trim() || (selectedChannel === 'email' && !subject.trim())}
                >
                  <Icon icon={FiSend} />
                  {sending ? 'Sending...' : `Send via ${selectedChannel.toUpperCase()}`}
                </button>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="mcs-info">
            <Icon icon={FiInfo} />
            <div>
              <strong>Note:</strong> Make sure communication channels are configured in Settings before sending messages.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MultiChannelMessageSender;

